import {
  ArrowFlightSQLAppInfoResponse,
  DefaultApi,
  FeatureFlag,
  LookerAppInfo,
  NetSuiteAppInfo,
  SnowflakeAppInfo,
  TableauAppInfo,
} from "@coeff/api";
import { DataSourceTypeDisplayNames } from "@coeff/utils";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, Menu, message } from "antd";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  Button,
  Form,
  ImportTypeIcon,
  List,
  LoaderWithPerfTimings,
  BreadcrumbItem,
  Breadcrumb,
  CaretRightBlack,
  Tooltip,
  AlertExclamation,
} from "../components";
import { COLORS } from "../constants";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils";

import { customOAuthRouteSlug } from "./CustomOAuthSettings";
import { Header } from "./Dashboard/components";

const Container = styled.div`
  padding: 25px 30px 48px;
  max-width: 660px;
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const StyledHeader = styled(Header)`
  margin: 0;
  h1 {
    margin: 0;
  }
`;

const FormGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  margin-bottom: 20px;

  h3 {
    font-weight: bold;
    margin: 12px 0;
  }
`;

const StyledSummaryText = styled.div`
  margin-right: 12px;
  color: ${COLORS.black45};
`;

const NoAppInfoContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 80px 20px;
  text-align: center;

  h3 {
    margin-bottom: 0;
  }
`;

const SupportedSystemsTooltip = styled.div`
  p {
    margin: 0 0 6px;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }
`;

/* Not an anchor: it explains rather than navigates, so a dotted underline rather than
   the solid one a real link would get. */
const SupportedSystemsHint = styled.span`
  border-bottom: 1px dotted ${COLORS.black45};
  cursor: pointer;
`;

/* Constrained so the connector list wraps as balanced centred lines rather than
   running the full width of the page. */
const NoAppInfoText = styled.div`
  color: ${COLORS.black45};
  margin-bottom: 20px;
  max-width: 420px;
`;

const { Item } = List;

const DataSourceSelections = styled(Link)`
  border-radius: 6px;
  display: block;
  color: inherit;

  &:hover {
    color: inherit;
  }

  .ant-list-item {
    .actionswrap {
      visibility: visible !important;
      border-radius: 6px !important;
    }
  }
`;

const StyledItem = styled(Item)`
  && {
    background: ${COLORS.black2};
    border: none;
    cursor: pointer;
    position: relative;
    margin-bottom: 8px;
    padding: 12px 12px;

    &:hover,
    &:focus-within,
    &:focus,
    &:active {
      background-color: ${COLORS.black4};

      .actionswrap {
        visibility: visible;
      }
    }

    .actionswrap {
      visibility: hidden;
    }

    .ant-list-item {
      margin: 12px 15px !important;
      border: 10px;
      background-color: ${COLORS.black4} !important;
      border-radius: 6px !important;
      cursor: default !important;

      .actionswrap {
        visibility: visible !important;
        border-radius: 6px !important;
      }
    }

    .ant-list-item-meta {
      align-items: center;
    }

    .ant-list-item-meta-title {
      color: ${COLORS.black45};
      line-height: 1.25;

      h4 {
        margin: 0px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }
    }

    .ant-list-item-meta-description {
      color: ${COLORS.black85};
      font-size: 14px;
      font-weight: bold;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
  }
`;

const ManageAction = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 16px;
`;

const StyledAlert = styled(Alert)`
  line-height: 1.8em;
  margin-top: 12px;
  padding: 12px;

  code {
    white-space: nowrap;
    background-color: ${COLORS.black4};
    padding: 4px 6px;
  }

  .ant-alert-icon {
    margin: 6px;
  }
`;

const StyledMenu = styled(Menu)`
  &.ant-menu-vertical {
    .ant-menu-item {
      height: auto;
      line-height: 1.5;
      margin: 0;
      padding: 10px 12px;
    }
  }
`;

const MenuLink = styled(Link)`
  align-items: center;
  color: inherit;
  display: flex;
  gap: 10px;

  &:hover {
    color: inherit;
  }
`;

const DropdownWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .ant-select {
    width: 50%;
  }
`;

const getLookerbaseUrl = (url: string) =>
  url?.replace(/^https?:\/\//, "").replace(/(:\d+)?\/?$/, "");

const fetchLookerAppInfo = async (api: DefaultApi): Promise<LookerAppInfo[] | null> => {
  const response = await api.listLookerAppInfo();
  if (response.data.apps_list.length) {
    return response.data.apps_list;
  }
  return null;
};

const fetchNetSuiteAppInfo = async (api: DefaultApi): Promise<NetSuiteAppInfo[] | null> => {
  const response = await api.listNetSuiteAppInfo();
  if (response.data.apps_list.length) {
    return response.data.apps_list;
  }
  return null;
};

const fetchSnowflakeAppInfo = async (api: DefaultApi): Promise<SnowflakeAppInfo[] | null> => {
  const response = await api.listSnowflakeAppInfo();
  if (response.data.apps_list.length) {
    return response.data.apps_list;
  }
  return null;
};

const fetchTableauAppInfo = async (api: DefaultApi): Promise<TableauAppInfo[] | null> => {
  const response = await api.listTableauAppInfo();
  if (response.data.apps_list.length) {
    return response.data.apps_list;
  }
  return null;
};

const fetchArrowFlightSqlAppInfo = async (
  api: DefaultApi
): Promise<ArrowFlightSQLAppInfoResponse[] | null> => {
  const response = await api.listArrowFlightSqlAppInfo();
  if (response.data.apps_list.length) {
    return response.data.apps_list;
  }
  return null;
};

export const OAuthConfigurations: React.FC = () => {
  const { clientFlags: clientFlags } = useSelector((state: AppState) => ({
    domains_in_domain_family: state.app.domains_in_domain_family,
    clientFlags: state.app.clientFlags,
  }));

  const [_, contextHolder] = message.useMessage();

  const track = useTrack();

  const { apiClient: api } = useApiContext();

  const { value: enabletableauOauthDirectTrustOption } = useFeatureGate(
    FeatureFlag.EnableTableauOauthDirectTrustOption
  );

  const { value: enableTableauOauthViaOkta } = useFeatureGate(
    FeatureFlag.EnableTableauOauthViaOkta
  );

  const isTableauOauthEnabled = enableTableauOauthViaOkta || enabletableauOauthDirectTrustOption;

  const { value: isArrowFlightSqlEnabled } = useFeatureGate(
    FeatureFlag.EnableArrowFlightSqlConnector
  );

  const fetchLookerAppInfoQuery = useQuery(
    [QueryCacheKey.LOOKER_APP_INFO],
    () => fetchLookerAppInfo(api),
    { cacheTime: 0 }
  );

  const {
    data: lookerAppInfos,
    isLoading: isFetchingLookerAppInfo,
    error: errorFetchingLookerAppInfos,
  } = fetchLookerAppInfoQuery;

  const fetchNetSuiteAppInfoQuery = useQuery(
    [QueryCacheKey.NETSUITE_APP_INFO],
    () => fetchNetSuiteAppInfo(api),
    { cacheTime: 0 }
  );

  const {
    data: netSuiteAppInfos,
    isLoading: isFetchingNetSuiteAppInfo,
    error: errorFetchingNetSuiteAppInfos,
  } = fetchNetSuiteAppInfoQuery;

  const fetchSnowflakeAppInfoQuery = useQuery(
    [QueryCacheKey.SNOWFLAKE_APP_INFO],
    () => fetchSnowflakeAppInfo(api),
    { cacheTime: 0 }
  );

  const {
    data: snowflakeAppInfos,
    isLoading: isFetchingSnowflakeAppInfo,
    error: errorFetchingSnowflakeAppInfos,
  } = fetchSnowflakeAppInfoQuery;

  const fetchTableauAppInfoQuery = useQuery(
    [QueryCacheKey.TABLEAU_APP_INFO],
    () => fetchTableauAppInfo(api),
    { cacheTime: 0 }
  );

  const {
    data: tableauAppInfos,
    isLoading: isFetchingTableauAppInfo,
    error: errorFetchingTableauAppInfos,
  } = fetchTableauAppInfoQuery;

  const fetchArrowFlightSqlAppInfoQuery = useQuery(
    [QueryCacheKey.ARROW_FLIGHT_SQL_APP_INFO],
    () => fetchArrowFlightSqlAppInfo(api),
    { cacheTime: 0, enabled: isArrowFlightSqlEnabled }
  );

  const {
    data: arrowFlightSqlAppInfos,
    isLoading: isFetchingArrowFlightSqlAppInfo,
    error: errorFetchingArrowFlightSqlAppInfos,
  } = fetchArrowFlightSqlAppInfoQuery;

  // Per-domain OAuth apps for bespoke connectors. One query serves all of them; the
  // response also names which data source types are configurable, so the menu below does
  // not need its own copy of spec.CUSTOM_OAUTH_DATA_SOURCE_TYPES.
  const { data: customOAuthData, isFetching: isFetchingCustomOAuthAppInfo } = useQuery(
    [QueryCacheKey.CUSTOM_OAUTH_APP_INFO],
    async () => (await api.listCustomOAuthAppInfo()).data,
    { cacheTime: 0 }
  );

  const customOAuthAppInfos = customOAuthData?.apps_list ?? [];
  const customOAuthDataSourceTypes = customOAuthData?.supported_data_source_types ?? [];

  useEffect(() => {
    document.title = "OAuth Settings - Coefficient Workspace";
    track("workspace_oauth_settings_viewed");
  }, []);

  // Arrow Flight SQL and every custom-OAuth connector are gated to specific domains, so
  // this section only renders for a domain that actually has one.
  const hasCustomIntegrations = isArrowFlightSqlEnabled || customOAuthDataSourceTypes.length > 0;

  const addAccountOptionsMenu = () => (
    <StyledMenu>
      <Menu.Item key="looker">
        <MenuLink to={`manage-looker-oauth?addNewAccount=true`}>
          <ImportTypeIcon type="looker" size="small" />
          {"Looker OAuth"}
        </MenuLink>
      </Menu.Item>
      <Menu.Item key="netsuite">
        <MenuLink to={`netsuite-oauth?addNewAccount=true`}>
          <ImportTypeIcon type="netsuite" size="small" />
          {"NetSuite OAuth"}
        </MenuLink>
      </Menu.Item>
      <Menu.Item key="snowflake">
        <MenuLink to={`snowflake-oauth?addNewAccount=true`}>
          <ImportTypeIcon type="snowflake" size="small" />
          {"Snowflake OAuth"}
        </MenuLink>
      </Menu.Item>
      {isTableauOauthEnabled && (
        <Menu.Item key="tableau">
          <MenuLink to={`tableau-oauth?addNewAccount=true`}>
            <ImportTypeIcon type="tableau" size="small" />
            {"Tableau OAuth"}
          </MenuLink>
        </Menu.Item>
      )}
      {hasCustomIntegrations && <Menu.Divider />}
      {isArrowFlightSqlEnabled && (
        <Menu.Item key="arrow_flight_sql">
          <MenuLink to={`arrow-flight-sql-oauth?addNewAccount=true`}>
            <ImportTypeIcon type="arrow_flight_sql" size="small" />
            {"Arrow Flight SQL OAuth"}
          </MenuLink>
        </Menu.Item>
      )}
      {customOAuthDataSourceTypes.map(dataSourceType => (
        <Menu.Item key={dataSourceType}>
          <MenuLink to={`${customOAuthRouteSlug(dataSourceType)}?addNewAccount=true`}>
            <ImportTypeIcon type={dataSourceType} size="small" />
            {`${DataSourceTypeDisplayNames[dataSourceType] ?? dataSourceType} OAuth`}
          </MenuLink>
        </Menu.Item>
      ))}
    </StyledMenu>
  );

  const renderAddAccountDropdown = (isLink: boolean) => {
    return (
      <DropdownWrapper>
        <Dropdown overlay={addAccountOptionsMenu()} trigger={["click"]}>
          <Button type={isLink ? "link" : "default"} noPadding={isLink}>
            Add account
          </Button>
        </Dropdown>
      </DropdownWrapper>
    );
  };

  // The empty state carries its own "Add account" control, so the header one would be a
  // second copy of the same button a few hundred pixels away.
  const renderContent = (children: React.ReactNode, hideAddAccount = false) => {
    return (
      <Container>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/admin`, search: window.location.search }}>Admin</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/admin/settings`, search: window.location.search }}>
              Settings
            </Link>
          </BreadcrumbItem>
        </Breadcrumb>
        <StyledHeaderContainer>
          <StyledHeader>
            <h1>OAuth Settings</h1>
          </StyledHeader>
          {!hideAddAccount && renderAddAccountDropdown(false)}
        </StyledHeaderContainer>
        {contextHolder}
        {children}
      </Container>
    );
  };

  if (clientFlags?.is_gmail_like_domain) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="Configuring OAuth accounts is not supported for your domain"
        description={`Configuring OAuth is not supported for ${
          clientFlags.email.split("@")[1]
        }. Please use a different account to configure OAuth accounts.`}
      />
    );
  }

  if (
    clientFlags?.admin_emails &&
    clientFlags?.admin_emails.length > 0 &&
    !clientFlags?.admin_emails?.includes(clientFlags.email)
  ) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="You need to be an admin to access this page"
        description="Please contact your domain admin for configuring or managing accounts."
      />
    );
  }

  if (
    isFetchingLookerAppInfo ||
    isFetchingNetSuiteAppInfo ||
    isFetchingSnowflakeAppInfo ||
    isFetchingCustomOAuthAppInfo ||
    (isTableauOauthEnabled && isFetchingTableauAppInfo) ||
    (isArrowFlightSqlEnabled && isFetchingArrowFlightSqlAppInfo)
  ) {
    // Hide the header button while loading: we do not yet know whether this lands on the
    // empty state, which carries its own, and showing it now makes it flash away.
    return renderContent(
      <div style={{ textAlign: "center", margin: "8rem auto" }}>
        <LoaderWithPerfTimings name="AdminOAuthSettings" verticalAlign />
      </div>,
      true
    );
  }

  if (
    errorFetchingLookerAppInfos ||
    errorFetchingNetSuiteAppInfos ||
    errorFetchingSnowflakeAppInfos ||
    (isTableauOauthEnabled && errorFetchingTableauAppInfos) ||
    (isArrowFlightSqlEnabled && errorFetchingArrowFlightSqlAppInfos)
  ) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="An error occurred while loading the page"
        description="Please try refreshing the page after a while. If the issue persists, please contact support@coefficient.io."
      />
    );
  }

  const noAppInfos =
    (!lookerAppInfos || lookerAppInfos.length === 0) &&
    (!netSuiteAppInfos || netSuiteAppInfos.length === 0) &&
    (!snowflakeAppInfos || snowflakeAppInfos.length === 0) &&
    (!isTableauOauthEnabled || !tableauAppInfos || tableauAppInfos.length === 0) &&
    (!isArrowFlightSqlEnabled || !arrowFlightSqlAppInfos || arrowFlightSqlAppInfos.length === 0) &&
    customOAuthAppInfos.length === 0;

  // Derived rather than hardcoded so a new custom-OAuth connector shows up here without
  // a second edit -- the same registry drives the "Add account" menu.
  const connectableNames = [
    "Looker",
    "NetSuite",
    "Snowflake",
    ...(isTableauOauthEnabled ? ["Tableau"] : []),
    ...(isArrowFlightSqlEnabled ? ["Arrow Flight SQL"] : []),
    ...customOAuthDataSourceTypes.map(type => DataSourceTypeDisplayNames[type] ?? type),
  ];

  if (noAppInfos) {
    return renderContent(
      <NoAppInfoContainer>
        <NoAppInfoText>
          {"Register a Coefficient OAuth client in a "}
          <Tooltip
            title={
              <SupportedSystemsTooltip>
                <p>
                  Coefficient supports custom OAuth client authentication for the following source
                  systems:
                </p>
                <ul>
                  {connectableNames.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </SupportedSystemsTooltip>
            }
          >
            <SupportedSystemsHint>supported source system</SupportedSystemsHint>
          </Tooltip>
          {" to simplify how your organization's users connect to it from Coefficient."}
        </NoAppInfoText>
        {renderAddAccountDropdown(false)}
      </NoAppInfoContainer>,
      true
    );
  }

  return renderContent(
    <div>
      <Form>
        <FormGroup>
          <h3>Configured Accounts</h3>
        </FormGroup>
      </Form>

      {lookerAppInfos &&
        lookerAppInfos.map((appInfo, index) => (
          <DataSourceSelections key={index} to={`looker-oauth?app_guid=${appInfo.client_app_guid}`}>
            <StyledItem>
              <Item.Meta
                title={DataSourceTypeDisplayNames.looker}
                avatar={<ImportTypeIcon type={"looker"} />}
                description={getLookerbaseUrl(appInfo.base_url)}
              />
              <ManageAction>
                <StyledSummaryText color="textSecondary">Manage</StyledSummaryText>
                <CaretRightBlack />
              </ManageAction>
            </StyledItem>
          </DataSourceSelections>
        ))}

      {netSuiteAppInfos &&
        netSuiteAppInfos.map((appInfo, index) => (
          <DataSourceSelections key={index} to={`netsuite-oauth/${appInfo.account_id}`}>
            <StyledItem>
              <Item.Meta
                title={DataSourceTypeDisplayNames.netsuite}
                avatar={<ImportTypeIcon type={"netsuite"} />}
                description={appInfo.account_id}
              />
              <ManageAction>
                <StyledSummaryText>Manage</StyledSummaryText>
                <CaretRightBlack />
              </ManageAction>
            </StyledItem>
          </DataSourceSelections>
        ))}

      {snowflakeAppInfos &&
        snowflakeAppInfos.map((appInfo, index) => (
          <DataSourceSelections key={index} to={`snowflake-oauth/${appInfo.account_id}`}>
            <StyledItem>
              <Item.Meta
                title={DataSourceTypeDisplayNames.snowflake}
                avatar={<ImportTypeIcon type={"snowflake"} />}
                description={appInfo.account_id}
              />
              <ManageAction>
                <StyledSummaryText>Manage</StyledSummaryText>
                <CaretRightBlack />
              </ManageAction>
            </StyledItem>
          </DataSourceSelections>
        ))}

      {isTableauOauthEnabled &&
        tableauAppInfos &&
        tableauAppInfos.map((appInfo, index) => (
          <DataSourceSelections
            key={index}
            to={`tableau-oauth/${encodeURIComponent(appInfo.client_id ?? "")}`}
          >
            <StyledItem>
              <Item.Meta
                title={DataSourceTypeDisplayNames.tableau}
                avatar={<ImportTypeIcon type={"tableau"} />}
                description={appInfo.app_name}
              />
              <ManageAction>
                <StyledSummaryText>Manage</StyledSummaryText>
                <CaretRightBlack />
              </ManageAction>
            </StyledItem>
          </DataSourceSelections>
        ))}

      {customOAuthAppInfos.map((appInfo, index) => (
        <DataSourceSelections
          key={`custom-oauth-${index}`}
          to={`${customOAuthRouteSlug(appInfo.data_source_type)}/${encodeURIComponent(
            appInfo.client_id
          )}`}
        >
          <StyledItem>
            <Item.Meta
              title={
                DataSourceTypeDisplayNames[appInfo.data_source_type] ?? appInfo.data_source_type
              }
              avatar={<ImportTypeIcon type={appInfo.data_source_type} />}
              description={appInfo.app_name || appInfo.base_url || appInfo.client_id}
            />
            <ManageAction>
              <StyledSummaryText>Manage</StyledSummaryText>
              <CaretRightBlack />
            </ManageAction>
          </StyledItem>
        </DataSourceSelections>
      ))}

      {isArrowFlightSqlEnabled &&
        arrowFlightSqlAppInfos &&
        arrowFlightSqlAppInfos.map((appInfo, index) => (
          <DataSourceSelections
            key={index}
            to={`arrow-flight-sql-oauth/${encodeURIComponent(appInfo.client_id)}`}
          >
            <StyledItem>
              <Item.Meta
                title={DataSourceTypeDisplayNames.arrow_flight_sql}
                avatar={<ImportTypeIcon type={"arrow_flight_sql"} />}
                description={appInfo.host}
              />
              <ManageAction>
                <StyledSummaryText>Manage</StyledSummaryText>
                <CaretRightBlack />
              </ManageAction>
            </StyledItem>
          </DataSourceSelections>
        ))}
    </div>
  );
};
